import { useCallback, useEffect, useState } from 'react';
import { Copy, Plus, RotateCcw, Search, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { taskTemplatesAPI, CATEGORY_LABELS, ENTITY_LABELS, PERIOD_LABELS } from '../../lib/taskTemplatesAPI';
import { ChecklistEditor } from './ChecklistEditor';
import { DeadlineRulesAdmin } from './DeadlineRulesAdmin';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const BLANK = { name: '', category: 'A', description: '', applicable_entity_types: [], default_priority: 'medium', period_type: 'none', checklist: [] };
const err = (e, f) => e?.response?.data?.detail || f;

const Editor = ({ tpl, onSaved }) => {
  const [f, setF] = useState(tpl);
  const [saving, setSaving] = useState(false);
  useEffect(() => setF(tpl), [tpl]);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, checklist: f.checklist.map(({ id, title, is_required }) => ({ id, title, is_required })) };
      const r = f.id ? await taskTemplatesAPI.update(f.id, body) : await taskTemplatesAPI.create(body);
      toast.success(`Saved (version ${r.data.version})`); onSaved(r.data.id);
    } catch (e) { toast.error(err(e, 'Save failed')); } finally { setSaving(false); }
  };
  const toggleEntity = (k, on) => setF({ ...f, applicable_entity_types: on ? [...f.applicable_entity_types, k] : f.applicable_entity_types.filter((x) => x !== k) });
  return (
    <div className="space-y-3" data-testid="template-editor">
      <div className="flex flex-wrap items-center gap-2">
        {f.source === 'system' && <Badge variant="secondary">System template</Badge>}
        {f.is_override && <Badge variant="outline">Customised for your practice</Badge>}
        {f.id && <span className="text-xs text-muted-foreground" data-testid="template-version">v{f.version}</span>}
      </div>
      <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="template-name-input" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{k}. {l}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Period type</Label><Select value={f.period_type} onValueChange={(v) => setF({ ...f, period_type: v })}><SelectTrigger data-testid="template-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PERIOD_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Default priority</Label><Select value={f.default_priority} onValueChange={(v) => setF({ ...f, default_priority: v })}><SelectTrigger data-testid="template-priority-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
      </div>
      <div><Label>Description</Label><Textarea rows={2} value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="template-description-input" /></div>
      <div><Label>Applies to</Label><div className="flex flex-wrap gap-3 mt-1">{Object.entries(ENTITY_LABELS).map(([k, l]) => (
        <label key={k} className="flex items-center gap-1.5 text-sm"><Checkbox checked={f.applicable_entity_types.includes(k)} onCheckedChange={(v) => toggleEntity(k, !!v)} data-testid={`template-entity-${k}`} />{l}</label>))}</div></div>
      <div><Label>Checklist ({f.checklist.length})</Label><ChecklistEditor items={f.checklist} onChange={(c) => setF({ ...f, checklist: c })} testPrefix="template-checklist" /></div>
      <Button onClick={save} disabled={saving || !f.name.trim()} data-testid="template-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}{f.id ? 'Save template' : 'Create template'}</Button>
      {f.source === 'system' && <p className="text-xs text-muted-foreground">Saving creates a practice copy. Existing tasks keep their own snapshot and are never changed.</p>}
    </div>
  );
};

// Owner/admin: Task Templates management (Tasks → Templates).
export const TaskTemplatesAdmin = ({ open, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const load = useCallback(async (selectId) => {
    const r = await taskTemplatesAPI.list({ include_inactive: true });
    setRows(r.data.templates);
    if (selectId !== undefined) setSel(r.data.templates.find((t) => t.id === selectId) || null);
    onChanged?.();
  }, [onChanged]);
  useEffect(() => { if (open) load().catch((e) => toast.error(err(e, 'Failed to load templates'))); }, [open]);
  const act = (fn, msg, pickId) => fn().then((r) => { toast.success(msg); return load(pickId ? r.data.id : sel?.id); }).catch((e) => toast.error(err(e, 'Action failed')));
  const shown = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="task-templates-admin">
        <DialogHeader><DialogTitle>Task Templates</DialogTitle><DialogDescription>Routine task types for your practice. Edits apply to new tasks only.</DialogDescription></DialogHeader>
        <Tabs defaultValue="templates" className="flex-1 min-h-0 flex flex-col">
        <TabsList className="self-start"><TabsTrigger value="templates" data-testid="admin-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="rules" data-testid="admin-tab-rules">Deadline rules</TabsTrigger></TabsList>
        <TabsContent value="rules" className="flex-1 min-h-0 overflow-y-auto pr-1"><DeadlineRulesAdmin templates={rows} /></TabsContent>
        <TabsContent value="templates" className="flex-1 min-h-0 data-[state=inactive]:hidden">
        <div className="h-full min-h-0 grid md:grid-cols-[320px_1fr] gap-4">
          <div className="flex flex-col min-h-0 border rounded-lg">
            <div className="p-2 flex gap-2 border-b">
              <div className="relative flex-1"><Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="h-8 pl-7" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates" data-testid="template-admin-search" /></div>
              <Button size="sm" className="h-8" onClick={() => setSel({ ...BLANK })} data-testid="template-new-btn"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto">{Object.keys(CATEGORY_LABELS).map((k) => {
              const list = shown.filter((t) => t.category === k);
              if (!list.length) return null;
              return (<div key={k}><p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{k}. {CATEGORY_LABELS[k]}</p>
                {list.map((t) => (
                  <button key={t.id} type="button" onClick={() => setSel(t)} data-testid={`template-row-${t.id}`}
                    className={cn('w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-muted/60', sel?.id === t.id && 'bg-muted', !t.is_active && 'opacity-50')}>
                    <span className="truncate flex-1">{t.name}</span>
                    {t.is_override && <span className="text-[10px] text-primary">custom</span>}
                    {t.source === 'practice' && !t.is_override && <span className="text-[10px] text-primary">practice</span>}
                    {!t.is_active && <span className="text-[10px]">hidden</span>}
                  </button>))}</div>);
            })}</div>
          </div>
          <div className="min-h-0 overflow-y-auto pr-1">
            {!sel ? <p className="text-sm text-muted-foreground p-6">Select a template to view or edit it.</p> : (
              <div className="space-y-3">
                {sel.id && (
                  <div className="flex flex-wrap items-center gap-2 border-b pb-3">
                    <label className="flex items-center gap-2 text-sm"><Switch checked={sel.is_active} onCheckedChange={(v) => act(() => taskTemplatesAPI.setActive(sel.id, v), v ? 'Template activated' : 'Template hidden', true)} data-testid="template-active-toggle" />Active</label>
                    <Button size="sm" variant="outline" onClick={() => act(() => taskTemplatesAPI.duplicate(sel.id), 'Template duplicated', true)} data-testid="template-duplicate-btn"><Copy className="w-3.5 h-3.5 mr-1" />Duplicate</Button>
                    {sel.is_override && <Button size="sm" variant="outline" onClick={() => window.confirm('Discard your practice changes and use the system default?') && act(() => taskTemplatesAPI.reset(sel.id), 'Reset to system default', true)} data-testid="template-reset-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset to system default</Button>}
                  </div>
                )}
                <Editor tpl={sel} onSaved={(id) => load(id)} />
              </div>
            )}
          </div>
        </div>
        </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
